import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { useLang } from '../hooks/useLang';
import './Dashboard.css';
import { generateShipmentReceipt, generateCargoManifest } from '../utils/generateCustomsDocs';

function getStatusLabels(t) {
  return {
    proposed: { label: t('dashboard.statusProposed'), class: 'badge-yellow' },
    rejected: { label: t('dashboard.statusRejected'), class: 'badge-gray' },
    pending: { label: t('dashboard.statusPending'), class: 'badge-yellow' },
    confirmed: { label: t('dashboard.statusConfirmed'), class: 'badge-green' },
    collected: { label: t('dashboard.statusCollected'), class: 'badge-green' },
    departed: { label: t('dashboard.statusDeparted'), class: 'badge-yellow' },
    delivered: { label: t('dashboard.statusDelivered'), class: 'badge-green' },
    cancelled: { label: t('dashboard.statusCancelled'), class: 'badge-gray' },
    could_not_deliver: { label: t('dashboard.statusCouldNotDeliver'), class: 'badge-yellow' },
    lost: { label: t('dashboard.statusLost'), class: 'badge-red' },
    seized_by_customs: { label: t('dashboard.statusSeized'), class: 'badge-red' },
    returned_to_sender: { label: t('dashboard.statusReturned'), class: 'badge-gray' },
  };
}

function getCompletionOutcomes(t) {
  return [
  { value: 'delivered', label: 'Delivered', emoji: '✅', desc: t('dashboard.outcomeDelivered') },
  { value: 'could_not_deliver', label: t('dashboard.statusCouldNotDeliver'), emoji: '📵', desc: t('dashboard.outcomeCouldNotDeliver') },
  { value: 'lost', label: t('dashboard.statusLost'), emoji: '🚨', desc: t('dashboard.outcomeLost') },
  { value: 'seized_by_customs', label: t('dashboard.statusSeized'), emoji: '🛃', desc: t('dashboard.outcomeSeized') },
  { value: 'returned_to_sender', label: t('dashboard.statusReturned'), emoji: '↩️', desc: t('dashboard.outcomeReturned') },
  ];
}

const PAYMENT_LABELS = {
  unpaid: { label: '⏳ Unpaid', color: '#e8775a' },
  paid:   { label: '✓ Paid',   color: '#2ecc71' },
  cash:   { label: '💵 Cash',  color: '#F5A623' },
  refunded: { label: '↩ Refunded', color: '#3498db' },
  partially_refunded: { label: '↩ Partial refund', color: '#3498db' },
};

const SENDER_CATEGORY_LABELS = {
  trustworthiness: '🔒 Trustworthiness',
  communication: '💬 Communication',
  reliability: '🤝 Reliability',
  item_declaration: '📋 Item declaration',
};

// ── SHARE UTILS ───────────────────────────────────────────────────────────────

function buildShareMessage(trip) {
  const originFlag = trip.origin_country === 'Tunisia' ? '🇹🇳' : '🇩🇪';
  const destFlag = trip.destination_country === 'Tunisia' ? '🇹🇳' : '🇩🇪';
  const departure = format(new Date(trip.departure_date), 'MMMM d, yyyy');
  const arrival = trip.arrival_date ? format(new Date(trip.arrival_date), 'MMMM d, yyyy') : null;
  const url = `${window.location.origin}/trip/${trip.id}`;

  const lines = [
    `✈️ Trip available on Transportily`,
    ``,
    `${originFlag} ${trip.origin_city} → ${destFlag} ${trip.destination_city}`,
    `📅 Departure: ${departure}`,
    arrival ? `📅 Arrival: ${arrival}` : null,
    ``,
    `📦 Details:`,
    `• Price: €${trip.price_per_kg}/kg`,
    `• Available capacity: ${trip.available_kg} kg`,
    trip.min_kg > 1 ? `• Minimum booking: ${trip.min_kg} kg` : null,
    trip.home_pickup ? `• 🏠 Home pickup offered (${trip.pickup_radius_km}km radius)` : null,
    trip.offers_pickup_location ? `• 📍 Fixed pickup location available` : null,
    trip.offers_home_delivery
      ? `• 🚚 Home delivery available${trip.home_delivery_fee > 0 ? ` (+€${trip.home_delivery_fee})` : ' (free)'}`
      : null,
    trip.accepts_fragile ? `• 🫧 Fragile items accepted` : null,
    trip.accepts_bulky ? `• 📦 Bulky items accepted` : null,
    trip.notes ? `\n📝 Note: ${trip.notes}` : null,
    ``,
    `👉 Book here: ${url}`,
  ].filter(l => l !== null);

  return lines.join('\n');
}

function ShareMenu({ trip, anchorRef, onClose }) {
  const menuRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 6,
        right: window.innerWidth - rect.right,
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    function handleClick(e) {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        anchorRef?.current && !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const url = `${window.location.origin}/trip/${trip.id}`;
  const message = buildShareMessage(trip);
  const encodedMsg = encodeURIComponent(message);

  function handleWhatsApp() {
    window.open(`https://wa.me/?text=${encodedMsg}`, '_blank');
    onClose();
  }

  function handleFacebook() {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`,
      '_blank'
    );
    onClose();
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => { setCopied(false); onClose(); }, 1400);
  }

  async function handleNative() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Trip ${trip.origin_city} → ${trip.destination_city}`,
          text: message,
          url,
        });
      } catch {}
    }
    onClose();
  }

  const options = [
    {
      label: 'WhatsApp',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      iconBg: '#25D366',
      iconColor: '#fff',
      action: handleWhatsApp,
    },
    {
      label: 'Facebook',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      iconBg: '#1877F2',
      iconColor: '#fff',
      action: handleFacebook,
    },
    {
      label: copied ? 'Copied!' : 'Copy message',
      icon: copied ? '✓' : '📋',
      iconBg: 'rgba(255,255,255,0.1)',
      iconColor: '#f5edd8',
      action: handleCopy,
    },
    ...(navigator.share
      ? [{
          label: 'More options',
          icon: '⬆️',
          iconBg: 'rgba(255,255,255,0.1)',
          iconColor: '#f5edd8',
          action: handleNative,
        }]
      : []),
  ];

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'absolute',
        top: pos.top,
        right: pos.right,
        background: '#1c1c1a',
        border: '1px solid rgba(255,255,255,0.13)',
        borderRadius: '12px',
        padding: '6px',
        zIndex: 9999,
        minWidth: '185px',
        boxShadow: '0 16px 40px rgba(0,0,0,0.85)',
      }}
    >
      {options.map(({ label, icon, iconBg, iconColor, action }) => (
        <button
          key={label}
          onClick={e => { e.stopPropagation(); action(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '9px 12px',
            background: 'none',
            border: 'none',
            color: '#f5edd8',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            borderRadius: '8px',
            textAlign: 'left',
            whiteSpace: 'nowrap',
            transition: 'background 0.12s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <span style={{
            width: '28px',
            height: '28px',
            borderRadius: '7px',
            background: iconBg,
            color: iconColor,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
          }}>
            {icon}
          </span>
          {label}
        </button>
      ))}
    </div>,
    document.body
  );
}

// ── TRIP ROW ──────────────────────────────────────────────────────────────────

function TripRow({ trip, shareMenuTripId, setShareMenuTripId, setSelectedTrip }) {
  const { t } = useLang();
  const STATUS_LABELS = getStatusLabels(t);
  const shareBtnRef = useRef(null);
  const pct = Math.round(((trip.total_kg - trip.available_kg) / trip.total_kg) * 100);
  const isActive = ['active', 'full'].includes(trip.status);
  const isInactive = ['cancelled', 'completed'].includes(trip.status);

  return (
    <div
      className="db-item db-trip-item db-item-clickable"
      onClick={() => setSelectedTrip(trip)}
      style={{
        opacity: isInactive ? 0.4 : 1,
        borderColor: isActive ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.06)',
        background: isActive ? 'rgba(46,204,113,0.04)' : undefined,
        transition: 'opacity 0.15s',
      }}
    >
      <div className="db-item-left">
        <div className="db-item-route" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span>{trip.origin_city} → {trip.destination_city}</span>
          {trip.is_multi_stop && <span style={{ fontSize: '10px', background: 'rgba(232,52,26,0.15)', border: '1px solid rgba(232,52,26,0.3)', color: 'var(--red)', borderRadius: '100px', padding: '1px 7px', fontWeight: '700' }}>{t('dashboard.multiStopBadge')}</span>}
        </div>
        <div className="db-item-meta">
          {format(new Date(trip.departure_date), 'MMM d, yyyy')} · €{trip.price_per_kg}/kg · {trip.available_kg} kg left
        </div>
        <div className="db-mini-bar">
          <div className="capacity-bar" style={{ height: '4px', flex: 1 }}>
            <div className="capacity-fill" style={{ width: pct + '%' }} />
          </div>
          <span style={{ fontSize: '11px', color: 'var(--olive)', whiteSpace: 'nowrap' }}>{pct}% booked</span>
        </div>
      </div>
      <div className="db-item-right">
        <span className={'badge ' + (trip.status === 'active' ? 'badge-green' : trip.status === 'full' ? 'badge-yellow' : 'badge-gray')}>{trip.status === 'full' ? '🔒 Full' : trip.status}</span>

        <div onClick={e => e.stopPropagation()}>
          <button
            ref={shareBtnRef}
            className="btn btn-ghost db-share-btn"
            onClick={e => {
              e.stopPropagation();
              setShareMenuTripId(shareMenuTripId === trip.id ? null : trip.id);
            }}
          >
            ↗ Share
          </button>
          {shareMenuTripId === trip.id && (
            <ShareMenu
              trip={trip}
              anchorRef={shareBtnRef}
              onClose={() => setShareMenuTripId(null)}
            />
          )}
        </div>

        <span style={{ color: 'var(--olive)', fontSize: '18px' }}>›</span>
      </div>
    </div>
  );
}

// ── SENDER REVIEWS ────────────────────────────────────────────────────────────

function SenderReviews({ senderId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('reviewee_id', senderId)
        .eq('reviewer_role', 'transporter')
        .order('created_at', { ascending: false })
        .limit(5);
      setReviews(data || []);
      setLoading(false);
    }
    if (senderId) fetch();
  }, [senderId]);

  if (loading) return null;

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));
  const avg = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="modal-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span className="modal-label" style={{ fontSize: '13px', fontWeight: '700', color: 'var(--sand)' }}>
          Sender reputation
        </span>
        {avg ? (
          <span style={{ fontSize: '13px', color: '#F5A623' }}>
            {'★'.repeat(Math.round(avg))} <span style={{ color: 'var(--olive)' }}>{avg} · {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
          </span>
        ) : (
          <span style={{ fontSize: '12px', color: 'var(--olive)' }}>No reviews yet</span>
        )}
      </div>
      {reviews.length === 0 && (
        <div style={{ fontSize: '13px', color: 'var(--olive)', padding: '8px 0' }}>
          This sender has no reviews from other transporters yet.
        </div>
      )}
      {reviews.map(r => (
        <div key={r.id} style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '13px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: '#F5A623' }}>{stars(r.rating)}</span>
            <span style={{ color: 'var(--olive)', fontSize: '11px' }}>{format(new Date(r.created_at), 'MMM d, yyyy')}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: r.comment ? '6px' : '0' }}>
            {Object.entries(SENDER_CATEGORY_LABELS).map(([key, label]) =>
              r[key] ? (
                <span key={key} style={{ fontSize: '11px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', padding: '2px 8px' }}>
                  {label} <span style={{ color: '#F5A623' }}>{'★'.repeat(r[key])}</span>
                </span>
              ) : null
            )}
          </div>
          {r.comment && (
            <p style={{ color: 'rgba(245,237,216,0.6)', margin: 0, lineHeight: '1.4' }}>"{r.comment}"</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── BOOKING MODAL ─────────────────────────────────────────────────────────────

function BookingModal({ booking, onClose, onStatusUpdate, isTransporter, unreadCount, hasReviewed, onCompletePayment, payingBookingId, transporterProfile }) {
  const { t } = useLang();
  const STATUS_LABELS = getStatusLabels(t);
  if (!booking) return null;
  const trip = booking.trips;
  const isUnpaid = booking.payment_status === 'unpaid' || !booking.payment_status;
  const isPaying = payingBookingId === booking.id;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{trip?.origin_city} → {trip?.destination_city}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!isTransporter && isUnpaid && (
            <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div>
                <div style={{ fontWeight: '700', color: '#e8775a', fontSize: '13px' }}>Payment required to confirm</div>
                <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.6)', marginTop: '2px' }}>Complete payment to confirm your booking. The transporter cannot confirm it manually.</div>
              </div>
            </div>
          )}
          {!isTransporter && !isUnpaid && booking.status !== 'cancelled' && booking.payment_status === 'paid' && (
            <div style={{ background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '12px', color: 'rgba(245,237,216,0.6)', lineHeight: '1.6' }}>
              <div style={{ fontWeight: '700', color: '#2ecc71', marginBottom: '4px' }}>✓ Booking confirmed</div>
              <div>If the transporter cancels this booking, you will receive a <strong style={{ color: 'var(--sand)' }}>full refund</strong> automatically.</div>
            </div>
          )}
          <div className="modal-section">
            <div className="modal-row">
              <span className="modal-label">Status</span>
              <span className={'badge ' + STATUS_LABELS[booking.status]?.class}>{STATUS_LABELS[booking.status]?.label}</span>
            </div>
            {!isTransporter && (
              <div className="modal-row">
                <span className="modal-label">Payment</span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: (() => {
                    // Cash booking that has been collected = effectively paid
                    if (booking.payment_status === 'cash' && ['collected','departed','delivered'].includes(booking.status))
                      return '#2ecc71';
                    return PAYMENT_LABELS[booking.payment_status]?.color || '#e8775a';
                  })(),
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '6px',
                  padding: '3px 8px',
                }}>
                  {booking.payment_status === 'cash' && ['collected','departed','delivered'].includes(booking.status)
                    ? '💵 Cash — paid'
                    : PAYMENT_LABELS[booking.payment_status]?.label || '⏳ Unpaid'}
                </span>
              </div>
            )}
            <div className="modal-row">
              <span className="modal-label">Booked on</span>
              <span>{booking.created_at && format(new Date(booking.created_at), 'MMM d, yyyy')}</span>
            </div>
            <div className="modal-row">
              <span className="modal-label">Payment method</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                {booking.sender_payment_mode === 'cash'
                  ? <><span>💵</span> Cash at pickup</>
                  : <><span>💳</span> Online payment</>
                }
              </span>
            </div>
            <div className="modal-row">
              <span className="modal-label">Departure</span>
              <span>{trip?.departure_date && format(new Date(trip.departure_date), 'MMMM d, yyyy')}</span>
            </div>
            <div className="modal-row">
              <span className="modal-label">{isTransporter ? 'Sender' : 'Transporter'}</span>
              {isTransporter ? (
                <Link to={`/sender/${booking.sender_id}`} style={{ color: 'var(--sand)', textDecoration: 'underline', textDecorationColor: 'rgba(245,237,216,0.3)' }}>
                  {booking.sender?.full_name || booking.profiles?.full_name || '—'}
                </Link>
              ) : (
                <Link to={`/transporter/${booking.trips?.transporter_id}`} style={{ color: 'var(--sand)', textDecoration: 'underline', textDecorationColor: 'rgba(245,237,216,0.3)' }}>
                  {trip?.profiles?.full_name}
                </Link>
              )}
            </div>
          </div>

          {isTransporter && <SenderReviews senderId={booking.sender_id} />}

          <div className="modal-section">
            {booking.kg_booked > 0 && (
              <div className="modal-row"><span className="modal-label">Weight</span><span>{booking.kg_booked} kg</span></div>
            )}
            {booking.document_count > 0 && (
              <div className="modal-row">
                <span className="modal-label">📄 Documents</span>
                <span>
                  {booking.document_count} × {
                    booking.document_type === 'official' ? 'Official document' :
                    booking.document_type === 'business' ? 'Business document' :
                    booking.document_type === 'letter'   ? 'Personal letter / envelope' :
                    booking.document_type === 'small_parcel' ? 'Small parcel (under 500g)' :
                    booking.document_type
                  }
                </span>
              </div>
            )}
            {booking.booking_type === 'document' && !booking.document_count && (
              <div className="modal-row"><span className="modal-label">Type</span><span>📄 Document booking</span></div>
            )}
            {booking.description && (
              <div className="modal-row"><span className="modal-label">Items</span><span>{booking.description}</span></div>
            )}
            {booking.pickup_address && (
              <div className="modal-row"><span className="modal-label">Pickup address</span><span>{booking.pickup_address}</span></div>
            )}
            {booking.dropoff_type && (
              <div className="modal-row">
                <span className="modal-label">Drop-off</span>
                <span>{booking.dropoff_type === 'home_delivery' ? '🚚 Home delivery' : '📍 Pickup location'}</span>
              </div>
            )}
            {booking.delivery_address && (
              <div className="modal-row"><span className="modal-label">Delivery address</span><span>{booking.delivery_address}</span></div>
            )}
          </div>

          {(booking.recipient_name || booking.recipient_phone) && (
            <div className="modal-section">
              {booking.recipient_name && (
                <div className="modal-row"><span className="modal-label">Recipient</span><span>{booking.recipient_name}</span></div>
              )}
              {booking.recipient_phone && (
                <div className="modal-row"><span className="modal-label">Recipient phone</span><span>{booking.recipient_phone}</span></div>
              )}
            </div>
          )}

          {/* Shipment progress tracker */}
          {['confirmed','collected','departed','delivered'].includes(booking.status) && (
            <div className="modal-section">
              <div className="modal-label" style={{ marginBottom: '10px' }}>Shipment progress</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', overflowX: 'auto' }}>
                {['confirmed','collected','departed','delivered'].map((s, i, arr) => {
                  const reached = ['confirmed','collected','departed','delivered'].indexOf(booking.status) >= i;
                  return (
                    <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <span style={{ fontSize: '11px', fontWeight: reached ? '700' : '400',
                        color: reached ? (s === 'delivered' ? '#2ecc71' : 'var(--sand)') : 'rgba(245,237,216,0.3)',
                        whiteSpace: 'nowrap' }}>
                        {s === 'confirmed' ? '✓ Confirmed' : s === 'collected' ? '📦 Collected' : s === 'departed' ? '✈️ Departed' : '🏁 Delivered'}
                      </span>
                      {i < arr.length - 1 && (
                        <span style={{ color: reached ? 'var(--olive)' : 'rgba(245,237,216,0.15)', fontSize: '11px' }}>—</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="modal-section">
            {/* Kg line — only if kg was booked */}
            {booking.kg_booked > 0 && trip?.price_per_kg && (
              <div className="modal-row">
                <span className="modal-label">📦 {booking.kg_booked} kg × €{trip.price_per_kg}</span>
                <span>€{(booking.kg_booked * trip.price_per_kg).toFixed(2)}</span>
              </div>
            )}
            {/* Document line — only if documents were booked */}
            {booking.document_count > 0 && (
              <div className="modal-row">
                <span className="modal-label">
                  📄 {booking.document_count} × {
                    booking.document_type === 'official'     ? 'Official doc' :
                    booking.document_type === 'business'     ? 'Business doc' :
                    booking.document_type === 'letter'       ? 'Letter/envelope' :
                    booking.document_type === 'small_parcel' ? 'Small parcel' :
                    'Document'
                  }
                </span>
                <span>€{((booking.total_price - booking.platform_fee) - (booking.kg_booked * (trip?.price_per_kg || 0))).toFixed(2)}</span>
              </div>
            )}
            <div className="modal-row"><span className="modal-label">Platform fee (10%)</span><span>€{booking.platform_fee?.toFixed(2)}</span></div>
            <div className="modal-row" style={{ fontWeight: '700', fontSize: '15px' }}>
              <span className="modal-label">Total</span><span>€{booking.total_price?.toFixed(2)}</span>
            </div>
            {booking.sender_payment_mode === 'cash' && (
              <>
                <div className="modal-row" style={{ color: '#4CAF7D' }}>
                  <span className="modal-label">💳 Due online (fee)</span>
                  <span>€{booking.platform_fee?.toFixed(2)}</span>
                </div>
                <div className="modal-row" style={{ color: '#F5A623' }}>
                  <span className="modal-label">💵 Due in cash at pickup</span>
                  <span>€{(booking.total_price - booking.platform_fee)?.toFixed(2)}</span>
                </div>
              </>
            )}
            {isTransporter && (
              <div className="modal-row" style={{ color: 'var(--red)', fontWeight: '700' }}>
                <span className="modal-label">You earn</span>
                <span>€{(booking.total_price - booking.platform_fee)?.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ flexDirection: 'column', gap: '8px' }}>
          {!isTransporter && isUnpaid && (
            <button
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={isPaying}
              onClick={() => { onCompletePayment(booking.id); onClose(); }}
            >
              {isPaying ? '↗ Redirecting to payment...' : '💳 Complete Payment'}
            </button>
          )}
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
          <Link to={'/chat/' + booking.id} className="btn btn-ghost" style={{ flex: 1 }}>
            💬 Chat {unreadCount > 0 && <span className="unread-dot">{unreadCount}</span>}
          </Link>
          {booking.status === 'delivered' && !hasReviewed && (
            <Link to={`/review/${booking.id}`} className="btn btn-outline" style={{ flex: 1 }}>⭐ Review</Link>
          )}
          {isTransporter && booking.status === 'pending' && (
            <>
              <button onClick={() => { onStatusUpdate(booking.id, 'confirmed'); onClose(); }} className="btn btn-primary" style={{ flex: 1 }}>{t('dashboard.confirmBtn')}</button>
              <button onClick={() => { onStatusUpdate(booking.id, 'cancelled', 'transporter'); onClose(); }} className="btn btn-ghost" style={{ flex: 1 }}>{t('dashboard.declineBtn')}</button>
            </>
          )}
          {isTransporter && booking.status === 'confirmed' && (
            <>
              <button onClick={() => { onStatusUpdate(booking.id, 'collected'); onClose(); }} className="btn btn-outline" style={{ flex: 1 }}>Mark Collected</button>
              <button
                onClick={() => {
                  if (window.confirm(t('dashboard.confirmCancelBooking'))) {
                    onStatusUpdate(booking.id, 'cancelled', 'transporter');
                    onClose();
                  }
                }}
                className="btn btn-ghost"
                style={{ flex: 1, color: 'var(--red)', borderColor: 'var(--red)' }}
              >{t('dashboard.cancelBooking')}</button>
            </>
          )}
          {isTransporter && booking.status === 'collected' && (
            <button onClick={() => { onStatusUpdate(booking.id, 'delivered'); onClose(); }} className="btn btn-outline" style={{ flex: 1 }}>Mark Delivered</button>
          )}
          {isTransporter && ['collected','departed','delivered'].includes(booking.status) && (
            <button
              className="btn btn-ghost"
              style={{ flex: 1, borderColor: 'var(--olive)', color: 'var(--olive)' }}
              onClick={() => generateShipmentReceipt(
                { ...booking, sender: booking.sender || booking.profiles || {} },
                { ...(booking.trips || {}), transporter: transporterProfile || {} }
              )}
            >📄 Customs Receipt</button>
          )}
          {!isTransporter && ['pending', 'confirmed'].includes(booking.status) && (
            <button
              onClick={() => {
                const msg = booking.payment_status === 'paid'
                  ? t('dashboard.confirmCancelBookingPaid')
                  : t('dashboard.confirmCancelBookingUnpaid');
                if (window.confirm(msg)) {
                  onStatusUpdate(booking.id, 'cancelled', 'sender');
                  onClose();
                }
              }}
              className="btn btn-ghost"
              style={{ flex: 1, color: 'var(--red)', borderColor: 'var(--red)' }}
            >{t('dashboard.cancelBooking')}</button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TRIP MODAL ────────────────────────────────────────────────────────────────

function CompleteTripModal({ trip, bookings, onClose, onCompleteTrip }) {
  const { t } = useLang();
  const COMPLETION_OUTCOMES = getCompletionOutcomes(t);
  if (!trip) return null;
  const activeBookings = bookings.filter(b =>
    b.trip_id === trip.id && !['cancelled'].includes(b.status)
  );
  const [outcomes, setOutcomes] = useState(() => {
    const init = {};
    activeBookings.forEach(b => { init[b.id] = 'delivered'; });
    return init;
  });
  const [notes, setNotes] = useState({});
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: '580px', width: '92vw', boxSizing: 'border-box', overflowX: 'hidden' }}>
        <div className="modal-header">
          <h2>Complete Trip</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ overflowX: 'hidden' }}>
          <p style={{ fontSize: '14px', color: 'rgba(245,237,216,0.6)', marginBottom: '20px' }}>
            Set the outcome for each booking. Senders will be notified by email.
          </p>

          {activeBookings.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--olive)', fontSize: '14px' }}>
              No active bookings to complete.
            </div>
          )}

          {activeBookings.map(b => (
            <div key={b.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '16px', marginBottom: '12px', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '14px' }}>{b.sender?.full_name || b.profiles?.full_name || 'Sender'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>
                    {b.kg_booked > 0 && <span>{b.kg_booked} kg</span>}
                    {b.document_count > 0 && <span>{b.kg_booked > 0 ? ' · ' : ''}{b.document_count} doc{b.document_count > 1 ? 's' : ''}</span>}
                    {b.description && <span> · {b.description?.slice(0, 40)}{b.description?.length > 40 ? '…' : ''}</span>}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--olive)' }}>€{b.total_price?.toFixed(2)}</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {COMPLETION_OUTCOMES.map(o => (
                  <label key={o.value} onClick={() => setOutcomes(prev => ({ ...prev, [b.id]: o.value }))} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                    background: outcomes[b.id] === o.value ? 'rgba(232,52,26,0.12)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${outcomes[b.id] === o.value ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: '8px', cursor: 'pointer', userSelect: 'none',
                    width: '100%', boxSizing: 'border-box',
                  }}>
                    <input
                      type="radio"
                      name={`outcome-${b.id}`}
                      value={o.value}
                      checked={outcomes[b.id] === o.value}
                      onChange={() => setOutcomes(prev => ({ ...prev, [b.id]: o.value }))}
                      style={{ flexShrink: 0, accentColor: 'var(--red)', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', flexShrink: 0, width: '20px', textAlign: 'center' }}>{o.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{o.label}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(245,237,216,0.45)', marginTop: '1px' }}>{o.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {['could_not_deliver', 'lost', 'seized_by_customs', 'returned_to_sender'].includes(outcomes[b.id]) && (
                <textarea
                  placeholder="Add a note for the sender (optional but recommended)..."
                  value={notes[b.id] || ''}
                  onChange={e => setNotes(prev => ({ ...prev, [b.id]: e.target.value }))}
                  rows={2}
                  style={{ width: '100%', marginTop: '10px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              )}
            </div>
          ))}
        </div>

        <div className="modal-footer" style={{ flexDirection: 'column', gap: '8px' }}>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={submitting || activeBookings.length === 0}
            onClick={async () => {
              setSubmitting(true);
              await onCompleteTrip(trip.id, outcomes, notes);
              setSubmitting(false);
              onClose();
            }}
          >
            {submitting ? t('dashboard.completingTrip') : t('dashboard.completeTrip')}
          </button>
          <button onClick={onClose} className="btn btn-ghost" style={{ width: '100%' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TripModal({ trip, bookings, onClose, onCancelTrip, onOpenCompleteTrip, onStatusUpdate }) {
  const { t } = useLang();
  const STATUS_LABELS = getStatusLabels(t);
  const [tripStops, setTripStops] = useState([]);
  useEffect(() => {
    if (trip?.is_multi_stop) {
      supabase.from('trip_stops').select('*').eq('trip_id', trip.id).order('sequence').then(({ data }) => setTripStops(data || []));
    } else {
      setTripStops([]);
    }
  }, [trip?.id]);

  if (!trip) return null;
  const pct = Math.round(((trip.total_kg - trip.available_kg) / trip.total_kg) * 100);
  const tripBookings = bookings.filter(b => b.trip_id === trip.id);
  const activeBookings = tripBookings.filter(b =>
    ['confirmed', 'collected', 'departed'].includes(b.status) ||
    (b.status === 'proposed' && b.payment_status === 'paid')
  );
  const canComplete = ['active', 'full'].includes(trip.status) && activeBookings.length > 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 style={{ marginBottom: trip.is_multi_stop ? '4px' : 0 }}>{trip.origin_city} → {trip.destination_city}</h2>
            {trip.is_multi_stop && (
              <div style={{ fontSize: '11px', color: 'var(--red)', fontWeight: '700', letterSpacing: '0.05em' }}>{t('dashboard.multiStopLabel')}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {trip.is_multi_stop && (
              <a href={`/trip/${trip.id}/itinerary`} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 10px' }}>
                📤 Share
              </a>
            )}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="modal-section">
            <div className="modal-row">
              <span className="modal-label">Status</span>
              <span className={'badge ' + (trip.status === 'active' ? 'badge-green' : trip.status === 'full' ? 'badge-yellow' : 'badge-gray')}>{trip.status === 'full' ? '🔒 Full' : trip.status}</span>
            </div>
            <div className="modal-row"><span className="modal-label">Departure</span><span>{format(new Date(trip.departure_date), 'MMMM d, yyyy')}</span></div>
            {trip.arrival_date && (
              <div className="modal-row"><span className="modal-label">Arrival</span><span>{format(new Date(trip.arrival_date), 'MMMM d, yyyy')}</span></div>
            )}
            <div className="modal-row"><span className="modal-label">Price per kg</span><span>€{trip.price_per_kg}</span></div>
            <div className="modal-row"><span className="modal-label">Capacity</span><span>{trip.available_kg} kg left of {trip.total_kg} kg</span></div>
            <div style={{ margin: '8px 0' }}>
              <div className="capacity-bar" style={{ height: '6px' }}><div className="capacity-fill" style={{ width: pct + '%' }} /></div>
              <span style={{ fontSize: '11px', color: 'var(--olive)' }}>{pct}% booked</span>
            </div>
          </div>

          {trip.is_multi_stop && tripStops.length > 0 && (
            <div className="modal-section">
              <div style={{ fontWeight: '700', fontSize: '12px', color: 'var(--olive)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('dashboard.stopItinerary')}</div>
              {tripStops.map((stop, idx) => {
                const typeColor = stop.stop_type === 'pickup' ? 'var(--red)' : stop.stop_type === 'delivery' ? '#4CAF7D' : '#6496ff';
                const typeIcon  = stop.stop_type === 'pickup' ? '📦' : stop.stop_type === 'delivery' ? '🏁' : '🚢';
                return (
                  <div key={stop.id} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', marginTop: '1px' }}>{typeIcon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                        <span style={{ fontWeight: '700', fontSize: '13px', color: typeColor }}>{stop.city}</span>
                        <span style={{ fontSize: '11px', color: 'var(--olive)' }}>
                          {stop.stop_date && format(new Date(stop.stop_date), 'MMM d')}
                          {stop.time_from && stop.time_until && ` · ${stop.time_from.slice(0,5)}–${stop.time_until.slice(0,5)}`}
                        </span>
                      </div>
                      {stop.address && <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '1px' }}>📍 {stop.address}</div>}
                      {stop.available_kg != null && stop.stop_type !== 'port' && (
                        <div style={{ fontSize: '11px', color: typeColor, fontWeight: '600' }}>⚖️ {stop.available_kg} kg left</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="modal-section">
            <div className="modal-row"><span className="modal-label">Min booking</span><span>{trip.min_kg} kg</span></div>
            {trip.offers_pickup_location && (
              <div className="modal-row"><span className="modal-label">📍 Pickup address</span><span>{trip.pickup_location_address || '—'}</span></div>
            )}
            {trip.offers_home_delivery && (
              <div className="modal-row"><span className="modal-label">🚚 Home delivery fee</span><span>€{trip.home_delivery_fee?.toFixed(2)}</span></div>
            )}
            {trip.home_pickup && (
              <div className="modal-row"><span className="modal-label">🏠 Sender pickup radius</span><span>{trip.pickup_radius_km} km</span></div>
            )}
            <div className="modal-row">
              <span className="modal-label">Accepts</span>
              <span>{[trip.accepts_fragile && 'Fragile', trip.accepts_bulky && 'Bulky'].filter(Boolean).join(', ') || 'Standard items only'}</span>
            </div>
            {trip.notes && (
              <div className="modal-row"><span className="modal-label">Notes</span><span style={{ color: 'rgba(245,237,216,0.6)' }}>{trip.notes}</span></div>
            )}
          </div>

          {tripBookings.length > 0 && (
            <div className="modal-section">
              <div className="modal-label" style={{ marginBottom: '10px' }}>Bookings ({tripBookings.length})</div>
              {tripBookings.map(b => {
                // Define which next-step action is available per booking status
                const nextAction =
                  (b.status === 'proposed' && b.payment_status === 'paid') ? { label: '✅ Confirm', next: 'confirmed', color: '#2ecc71' } :
                  b.status === 'confirmed' ? { label: '📦 Mark Collected', next: 'collected', color: 'var(--sand)' } :
                  b.status === 'collected' ? { label: '✈️ Mark Departed', next: 'departed', color: 'var(--sand)' } :
                  b.status === 'departed'  ? { label: '🏁 Mark Delivered', next: 'delivered', color: '#2ecc71' } :
                  null;

                return (
                  <div key={b.id} style={{ padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: '13px' }}>
                    {/* Header row: name + status badges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: nextAction ? '10px' : '0' }}>
                      <div>
                        <span style={{ fontWeight: '600' }}>{b.sender?.full_name || b.profiles?.full_name || 'Sender'}</span>
                        <span style={{ color: 'var(--olive)', marginLeft: '6px' }}>{b.kg_booked > 0 ? `${b.kg_booked} kg` : ''}{b.document_count > 0 ? `${b.kg_booked > 0 ? ' + ' : ''}${b.document_count} doc` : ''} · €{b.total_price?.toFixed(2)}</span>
                        {(b.sender?.phone || b.profiles?.phone) && (
                          <span style={{ display: 'block', fontSize: '11px', color: 'rgba(245,237,216,0.45)', marginTop: '2px' }}>
                            📞 {b.sender?.phone || b.profiles?.phone}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className={'badge ' + STATUS_LABELS[b.status]?.class}>{STATUS_LABELS[b.status]?.label}</span>
                        {b.payment_status && PAYMENT_LABELS?.[b.payment_status] && (
                          <span style={{ fontSize: '11px', color: PAYMENT_LABELS[b.payment_status].color, fontWeight: '600' }}>
                            {PAYMENT_LABELS[b.payment_status].label}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Progress tracker */}
                    {['confirmed','collected','departed','delivered'].includes(b.status) && (
                      <div className="booking-progress">
                        {[
                          { s: 'confirmed', label: 'Confirmed' },
                          { s: 'collected', label: 'Collected' },
                          { s: 'departed',  label: 'Departed'  },
                          { s: 'delivered', label: 'Delivered' },
                        ].map(({ s, label }, i, arr) => {
                          const statuses = ['confirmed','collected','departed','delivered'];
                          const currentIdx = statuses.indexOf(b.status);
                          const stepIdx = statuses.indexOf(s);
                          const done = stepIdx <= currentIdx;
                          return (
                            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div style={{
                                width: '8px', height: '8px', borderRadius: '50%',
                                background: done ? '#2ecc71' : 'rgba(255,255,255,0.15)',
                                flexShrink: 0,
                              }} />
                              <span style={{ fontSize: '11px', color: done ? '#2ecc71' : 'rgba(245,237,216,0.35)', whiteSpace: 'nowrap' }}>{label}</span>
                              {i < arr.length - 1 && (
                                <div style={{ width: '16px', height: '1px', background: done && stepIdx < currentIdx ? '#2ecc71' : 'rgba(255,255,255,0.1)', margin: '0 2px' }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Paid proposal — webhook fallback */}
                    {b.status === 'proposed' && b.payment_status === 'paid' && (
                      <div style={{ padding: '7px 10px', background: 'rgba(46,204,113,0.07)', border: '1px solid rgba(46,204,113,0.2)', borderRadius: '8px', marginBottom: '8px', fontSize: '12px', color: '#2ecc71' }}>
                        💳 Payment received — please confirm to proceed
                      </div>
                    )}
                    {/* Next-step action button */}
                    {nextAction && (
                      <button
                        className="btn btn-outline"
                        style={{ fontSize: '12px', padding: '6px 14px', borderColor: nextAction.color, color: nextAction.color, width: '100%' }}
                        onClick={() => onStatusUpdate && onStatusUpdate(b.id, nextAction.next)}
                      >
                        {nextAction.label}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── CUSTOMS DOCUMENTS ─────────────────────────────────────────────── */}
        {tripBookings.some(b => ['collected','departed','delivered'].includes(b.status)) && (
          <div className="customs-docs-section">
            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--sand)', marginBottom: '8px', letterSpacing: '0.05em' }}>
              {t('dashboard.customsDocs')}
            </div>
            <div className="customs-docs-buttons">
              <button
                className="btn btn-ghost"
                style={{ fontSize: '12px', borderColor: 'var(--olive)', color: 'var(--sand)', gap: '6px' }}
                onClick={() => {
                  const collectedBookings = tripBookings.filter(b =>
                    ['collected','departed','delivered'].includes(b.status)
                  );
                  // Enrich trip with transporter profile from booking's trips join (has profiles!transporter_id)
                  const tripWithProfile = {
                    ...trip,
                    transporter: (collectedBookings[0]?.trips?.profiles) || trip.profiles || {},
                  };
                  generateCargoManifest(tripWithProfile, collectedBookings);
                }}
              >
                {t('dashboard.fullManifest')}
              </button>
              {tripBookings
                .filter(b => ['collected','departed','delivered'].includes(b.status))
                .map(b => {
                  const senderName = (b.sender?.full_name || b.profiles?.full_name || 'Sender').split(' ')[0];
                  return (
                    <button
                      key={b.id}
                      className="btn btn-ghost"
                      style={{ fontSize: '12px', borderColor: 'rgba(138,132,104,0.4)', color: 'var(--olive)', gap: '6px' }}
                      onClick={() => generateShipmentReceipt(
                        { ...b, sender: b.sender || b.profiles || {} },
                        { ...trip, transporter: (b.trips?.profiles) || trip.profiles || {} }
                      )}
                    >
                      📄 {senderName}'s Receipt
                    </button>
                  );
                })
              }
            </div>
            <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '6px' }}>
              Available after goods are collected · Bilingual EN/FR for Tunisian customs
            </div>
          </div>
        )}

        {['active', 'full'].includes(trip.status) && (
          <div className="modal-footer" style={{ flexDirection: 'column', gap: '8px' }}>
            {canComplete && (
              <button
                className="btn btn-primary"
                style={{ width: '100%' }}
                onClick={() => { onClose(); onOpenCompleteTrip(trip); }}
              >
                ✅ Complete Trip
              </button>
            )}
            {!canComplete && (
              <div style={{ fontSize: '13px', color: 'var(--olive)', textAlign: 'center', padding: '8px 0' }}>
                No confirmed bookings to complete yet.
              </div>
            )}
            <button
              onClick={() => {
                if (window.confirm(t('dashboard.confirmCancelTrip'))) {
                  onCancelTrip(trip.id);
                  onClose();
                }
              }}
              className="btn btn-ghost"
              style={{ width: '100%' }}
            >{t('dashboard.cancelTrip')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, profile } = useAuth();
  const location = useLocation();

  const [tab, setTabState] = useState(new URLSearchParams(location.search).get('tab') || 'bookings');
  function setTab(t) {
    setTabState(t);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; }, { replace: true });
  }

  useEffect(() => {
    if (!new URLSearchParams(location.search).get('tab') && profile?.role) {
      setTab(profile.role === 'transporter' || profile.role === 'both' ? 'trips' : 'bookings');
    }
  }, [profile?.role]);

  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [tripBookings, setTripBookings] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [reviewedBookingIds, setReviewedBookingIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [shareMenuTripId, setShareMenuTripId] = useState(null);
  const [payingBookingId, setPayingBookingId] = useState(null);
  const [completeTripData, setCompleteTripData] = useState(null);
  const [showArchivedTrips, setShowArchivedTrips] = useState(false);
  const [proposals, setProposals] = useState([]); // transporter's outgoing proposals
  const [senderProposals, setSenderProposals] = useState([]);
  const [senderMyRequests, setSenderMyRequests] = useState([]); // sender's incoming proposals per request
  const [favorites, setFavorites] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalForm, setWithdrawalForm] = useState({ account_holder: '', bank_country: 'DE', iban: '', bic: '', bank_name: '', account_number: '' });
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());

  function exportEarningsReport(year) {
    const filtered = walletTransactions.filter(tx => {
      const txYear = new Date(tx.created_at).getFullYear();
      return txYear === year;
    });

    if (filtered.length === 0) {
      alert(`No transactions found for ${year}.`);
      return;
    }

    const TYPE_LABELS = {
      booking_credit:    'Online booking payment',
      cash_credit:       'Cash collected',
      refund_debit:      'Refund issued',
      withdrawal_debit:  'Withdrawal',
      adjustment:        'Adjustment',
      platform_fee_debit:'Platform fee',
    };

    const totalCredit = filtered.filter(t => t.direction === 'credit').reduce((s,t) => s + t.amount, 0);
    const totalDebit  = filtered.filter(t => t.direction === 'debit').reduce((s,t) => s + t.amount, 0);
    const onlineCredit = filtered.filter(t => t.type === 'booking_credit').reduce((s,t) => s + t.amount, 0);
    const cashCredit   = filtered.filter(t => t.type === 'cash_credit').reduce((s,t) => s + t.amount, 0);
    const totalWithdrawals = filtered.filter(t => t.type === 'withdrawal_debit').reduce((s,t) => s + t.amount, 0);
    const totalRefunds = filtered.filter(t => t.type === 'refund_debit').reduce((s,t) => s + t.amount, 0);

    const name = profile?.full_name || user?.email || 'Transporter';
    const now = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Build CSV
    const rows = [
      ['TRANSPORTILY — EARNINGS REPORT'],
      [`Transporter: ${name}`],
      [`Tax Year: ${year}`],
      [`Generated: ${now}`],
      [`Email: ${user?.email || ''}`],
      [],
      ['SUMMARY'],
      ['Total earnings (credit)', `EUR ${totalCredit.toFixed(2)}`],
      ['  Online payments', `EUR ${onlineCredit.toFixed(2)}`],
      ['  Cash collected', `EUR ${cashCredit.toFixed(2)}`],
      ['Total deductions (debit)', `EUR ${totalDebit.toFixed(2)}`],
      ['  Refunds issued', `EUR ${totalRefunds.toFixed(2)}`],
      ['  Withdrawals', `EUR ${totalWithdrawals.toFixed(2)}`],
      ['Net income', `EUR ${(totalCredit - totalDebit).toFixed(2)}`],
      [],
      ['TRANSACTIONS'],
      ['Date', 'Time', 'Type', 'Method', 'Direction', 'Amount (EUR)', 'Description', 'Booking ID'],
      ...filtered.map(tx => [
        new Date(tx.created_at).toLocaleDateString('en-GB'),
        new Date(tx.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        TYPE_LABELS[tx.type] || tx.type,
        tx.type === 'cash_credit' ? 'Cash' : tx.type === 'booking_credit' ? 'Online' : '—',
        tx.direction === 'credit' ? 'Credit' : 'Debit',
        tx.amount.toFixed(2),
        (tx.description || '').replace(/,/g, ';'),
        tx.booking_id || '',
      ])
    ];

    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `transportily-earnings-${year}-${name.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [withdrawalSubmitting, setWithdrawalSubmitting] = useState(false);
  const [withdrawalSuccess, setWithdrawalSuccess] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('payment') === 'success';
  const paymentBookingId = searchParams.get('booking');
  const cashBookingSuccess = searchParams.get('cash') === '1';

  useEffect(() => {
    if (paymentSuccess && paymentBookingId) {
      // Mark the shipment_request as filled immediately (don't wait for webhook)
      (async () => {
        const { data: b } = await supabase
          .from('bookings')
          .select('shipment_request_id, status, payment_status')
          .eq('id', paymentBookingId)
          .maybeSingle();
        if (b?.shipment_request_id) {
          await supabase
            .from('shipment_requests')
            .update({ status: 'filled' })
            .eq('id', b.shipment_request_id);
        }
        // Also confirm the booking if webhook hasn't fired yet
        if (b && b.status === 'proposed') {
          await supabase
            .from('bookings')
            .update({ status: 'confirmed', payment_status: 'paid' })
            .eq('id', paymentBookingId);
        }
      })();
      // Re-fetch so updated state appears immediately
      fetchData();
      const timer = setTimeout(() => {
        setSearchParams({});
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess]);

  useEffect(() => { fetchData(); }, [user]);

  useEffect(() => {
    const channel = supabase
      .channel('dashboard-messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        fetchUnreadCounts([...bookings, ...tripBookings]);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [bookings, tripBookings]);

  async function fetchData() {
    setLoading(true);
    const [bRes, tRes] = await Promise.all([
      supabase.from('bookings')
        .select('*, payment_status, booking_type, document_type, document_count, trips(origin_city, destination_city, departure_date, price_per_kg, document_price, origin_country, destination_country, transporter_id, profiles!transporter_id(full_name))')
        .eq('sender_id', user.id)
        .order('created_at', { ascending: false }),
      supabase.from('trips').select('*').eq('transporter_id', user.id).order('departure_date', { ascending: true }),
    ]);

    const freshBookings = bRes.data || [];
    const freshTrips = tRes.data || [];
    setBookings(freshBookings);
    setTrips(freshTrips);

    let freshTripBookings = [];
    if (freshTrips.length > 0) {
      const tripIds = freshTrips.map(t => t.id);
      const { data: tb } = await supabase
        .from('bookings')
        .select('*, sender:profiles!sender_id(full_name, phone, avatar_url, rating, total_reviews, is_verified), trips(id, origin_city, destination_city, departure_date, price_per_kg, origin_country, destination_country, transporter_id, available_kg, total_kg, profiles!transporter_id(full_name, phone))')
        .in('trip_id', tripIds)
        .order('created_at', { ascending: false });
      freshTripBookings = tb || [];
      setTripBookings(freshTripBookings);
    }

    const allBookingIds = [...freshBookings, ...freshTripBookings].map(b => b.id);
    if (allBookingIds.length > 0) {
      const { data: existingReviews } = await supabase
        .from('reviews')
        .select('booking_id')
        .eq('reviewer_id', user.id)
        .in('booking_id', allBookingIds);
      setReviewedBookingIds(new Set((existingReviews || []).map(r => r.booking_id)));
    }

    // Fetch transporter's outgoing proposals via their trips
    if (profile?.role === 'transporter' || profile?.role === 'both') {
      const myTripIds = freshTrips.map(t => t.id);
      if (myTripIds.length > 0) {
        const { data: proposalData } = await supabase
          .from('bookings')
          .select('*, trips(origin_city, destination_city, departure_date, origin_country, destination_country, available_kg, status), shipment_requests(origin_city, destination_city, kg_needed, needed_by_date, description, sender_id, profiles!sender_id(full_name))')
          .in('trip_id', myTripIds)
          .eq('status', 'proposed')
          .not('shipment_request_id', 'is', null)
          .order('created_at', { ascending: false });
        setProposals(proposalData || []);
      } else {
        setProposals([]);
      }
    }

    // Fetch sender's requests + incoming proposals
    if (profile?.role === 'sender' || profile?.role === 'both') {
      const { data: myRequestsData } = await supabase
        .from('shipment_requests')
        .select('*')
        .eq('sender_id', user.id)
        .in('status', ['open', 'filled'])
        .order('created_at', { ascending: false });
      const myReqList = myRequestsData || [];
      const reqIds = myReqList.map(r => r.id);

      let incomingProposals = [];
      if (reqIds.length > 0) {
        const { data: incomingData } = await supabase
          .from('bookings')
          .select('*, trips(id, origin_city, destination_city, departure_date, price_per_kg, origin_country, destination_country, transporter_id, available_kg, profiles!transporter_id(id, full_name, rating, total_reviews, is_verified)), shipment_requests(id, origin_city, destination_city, kg_needed, needed_by_date)')
          .in('shipment_request_id', reqIds)
          .eq('status', 'proposed')
          .order('created_at', { ascending: false });
        incomingProposals = incomingData || [];
      }
      setSenderProposals(incomingProposals);
      // Store requests separately so we can show them even with no proposals
      setSenderMyRequests(myReqList);
    }

    if (profile?.role === 'transporter' || profile?.role === 'both') {
      const { data: walletData } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      setWallet(walletData);

      // Always fetch transactions — even if no wallet yet (cash-only transporter)
      const { data: txData } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);
      setWalletTransactions(txData || []);
    }

    setLoading(false);
    fetchUnreadCounts([...freshBookings, ...freshTripBookings]);
  }

  async function fetchUnreadCounts(allBookings) {
    const allBookingIds = allBookings.map(b => b.id);
    if (allBookingIds.length === 0) return;
    const { data } = await supabase
      .from('messages')
      .select('booking_id')
      .in('booking_id', allBookingIds)
      .eq('read', false)
      .neq('sender_id', user.id);
    const counts = {};
    (data || []).forEach(msg => {
      counts[msg.booking_id] = (counts[msg.booking_id] || 0) + 1;
    });
    setUnreadCounts(counts);
  }

  // Transporter withdraws a proposal before sender pays
  async function handleWithdrawProposal(booking) {
    if (!window.confirm(t('dashboard.confirmWithdrawProposal'))) return;
    await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', booking.id);
    // Note: available_kg is restored automatically by the DB trigger on_booking_status_change
    // Note: if marked_full, trip was never confirmed so no kg was deducted — just reset status
    if (booking.marked_full && booking.trip_id) {
      await supabase.from('trips').update({ status: 'active' }).eq('id', booking.trip_id);
    }
    setProposals(p => p.filter(p => p.id !== booking.id));
  }

  // Sender cancels a request from the dashboard
  async function handleCancelRequestFromDashboard(reqId) {
    if (!window.confirm(t('dashboard.confirmCancelRequest'))) return;
    await supabase.from('shipment_requests').update({ status: 'cancelled' }).eq('id', reqId);
    const { data: proposals } = await supabase
      .from('bookings').select('id, trip_id, kg_booked, marked_full, trips(transporter_id)')
      .eq('shipment_request_id', reqId).eq('status', 'proposed');
    for (const p of proposals || []) {
      await supabase.from('bookings').update({ status: 'rejected' }).eq('id', p.id);
      if (p.marked_full && p.trip_id) await supabase.from('trips').update({ status: 'cancelled' }).eq('id', p.trip_id);
      if (p.trips?.transporter_id) {
        await supabase.functions.invoke('send-notification', {
          body: { notification_context: 'proposal_rejected', notify_user_id: p.trips.transporter_id, record: { id: p.id, trip_id: p.trip_id, marked_full: p.marked_full } },
        });
      }
    }
    setSenderMyRequests(r => r.filter(x => x.id !== reqId));
    setSenderProposals(r => r.filter(x => x.shipment_request_id !== reqId));
  }

  // Sender accepts a proposal — redirects to payment (checkout session)
  async function handleAcceptProposal(booking) {
    setCheckoutLoading(booking.id);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { booking_id: booking.id, sender_id: user.id },
      });
      if (error || !data?.url) throw new Error(error?.message || 'No checkout URL');
      window.location.href = data.url;
    } catch (err) {
      alert(t('dashboard.alertPaymentStartError'));
      setCheckoutLoading(null);
    }
  }

  async function updateBookingStatus(bookingId, status, cancelledBy = null) {
    const booking = [...bookings, ...tripBookings].find(b => b.id === bookingId);

    // Route any paid booking (online 'paid' or cash platform-fee 'cash') through refund-booking
    // refund-booking handles: Stripe refund + kg restoration + wallet debit
    const hasPaidStripe = booking?.payment_status === 'paid' || booking?.payment_status === 'cash';
    if (status === 'cancelled' && hasPaidStripe && cancelledBy) {
      try {
        await supabase.functions.invoke('refund-booking', {
          body: { booking_id: bookingId, cancelled_by: cancelledBy },
        });
        fetchData();
        return;
      } catch (err) {
        console.error('Refund error:', err);
      }
    }

    await supabase.from('bookings').update({ status }).eq('id', bookingId);

    // When transporter marks a CASH booking as collected → record in transaction history
    // DO NOT call credit_wallet (that increments wallet.balance = online balance)
    // Cash earnings are tracked separately and shown in the cash card
    // We only insert a transaction record for history visibility
    if (status === 'collected' && booking?.sender_payment_mode === 'cash' && booking?.payment_status === 'cash') {
      // Use SECURITY DEFINER RPC — validates ownership server-side, no direct table access
      const { data: result, error: rpcError } = await supabase.rpc('record_cash_collection', {
        p_booking_id: bookingId,
      });
      if (rpcError) {
        console.error('Cash collection RPC error:', rpcError);
      } else if (result?.ok) {
        // Refresh transactions and wallet
        const { data: freshTx } = await supabase
          .from('wallet_transactions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        setWalletTransactions(freshTx || []);
        const { data: freshWallet } = await supabase.from('wallets').select('*').eq('user_id', user.id).maybeSingle();
        if (freshWallet) setWallet(freshWallet);
      }
    }

    // NOTE: available_kg is managed entirely by the DB trigger on_booking_status_change.
    // The trigger deducts on confirmed and restores on cancelled.
    // We only handle trip-level STATUS here (full / active / cancelled).
    if (booking?.trip_id) {
      if (status === 'confirmed') {
        // Check if trip should be marked full (trigger already decremented available_kg)
        const { data: freshTrip } = await supabase
          .from('trips').select('available_kg, status').eq('id', booking.trip_id).single();
        if (freshTrip && freshTrip.available_kg <= 0 && freshTrip.status !== 'full') {
          await supabase.from('trips').update({ status: 'full' }).eq('id', booking.trip_id);
        }
      } else if (status === 'cancelled') {
        // Check remaining active bookings to decide trip fate
        const { data: remainingBookings } = await supabase
          .from('bookings').select('id, shipment_request_id')
          .eq('trip_id', booking.trip_id)
          .not('status', 'in', '(cancelled,rejected)');
        const others = (remainingBookings || []).filter(b => b.id !== bookingId);
        const { data: freshTrip } = await supabase
          .from('trips').select('available_kg, total_kg, status').eq('id', booking.trip_id).single();
        if (others.length === 0 && booking.shipment_request_id) {
          // Trip created solely for this request → cancel it
          await supabase.from('trips').update({ status: 'cancelled' }).eq('id', booking.trip_id);
        } else if (freshTrip && freshTrip.status === 'full') {
          // Reopen trip (trigger already restored available_kg)
          await supabase.from('trips').update({ status: 'active' }).eq('id', booking.trip_id);
        }
      }
    }

    // ── AUTO-COMPLETE TRIP ───────────────────────────────────────────────────
    // Only mark trip completed if ALL bookings are terminal AND at least one
    // was actually delivered (not just all cancelled — that should keep the trip active)
    const TERMINAL = ['delivered', 'could_not_deliver', 'lost', 'seized_by_customs', 'returned_to_sender', 'cancelled', 'rejected'];
    const POSITIVE_TERMINAL = ['delivered', 'could_not_deliver', 'lost', 'seized_by_customs', 'returned_to_sender'];
    if (TERMINAL.includes(status) && booking?.trip_id) {
      const { data: allTripBookings } = await supabase
        .from('bookings')
        .select('id, status')
        .eq('trip_id', booking.trip_id);

      const effectiveStatuses = (allTripBookings || []).map(b =>
        b.id === bookingId ? status : b.status
      );

      const allDone = effectiveStatuses.length > 0 &&
        effectiveStatuses.every(s => TERMINAL.includes(s));

      // Don't auto-complete if every booking was simply cancelled/rejected
      const hasPositiveOutcome = effectiveStatuses.some(s => POSITIVE_TERMINAL.includes(s));

      if (allDone && hasPositiveOutcome) {
        await supabase.from('trips').update({ status: 'completed' }).eq('id', booking.trip_id);
      } else if (allDone && !hasPositiveOutcome) {
        // All cancelled — reopen the trip so the transporter can reuse it
        await supabase.from('trips').update({ status: 'active' }).eq('id', booking.trip_id);
      }
    }

    fetchData();
  }

  async function completePayment(bookingId) {
    setPayingBookingId(bookingId);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { booking_id: bookingId, sender_id: user.id },
      });
      if (error || !data?.url) {
        alert(t('dashboard.alertPaymentFailed'));
        setPayingBookingId(null);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      alert(t('dashboard.alertPaymentError'));
      setPayingBookingId(null);
    }
  }

  async function cancelTrip(tripId) {
    await supabase.from('trips').update({ status: 'cancelled' }).eq('id', tripId);
    const activeBookings = tripBookings.filter(
      b => b.trip_id === tripId && !['cancelled', 'delivered'].includes(b.status)
    );
    for (const booking of activeBookings) {
      const hasPaidStripe = booking.payment_status === 'paid' || booking.payment_status === 'cash';
      if (hasPaidStripe) {
        await supabase.functions.invoke('refund-booking', {
          body: { booking_id: booking.id, cancelled_by: 'transporter' },
        });
      } else {
        await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', booking.id);
      }
    }
    fetchData();
  }

  async function completeTrip(tripId, outcomes, notes) {
    for (const [bookingId, outcome] of Object.entries(outcomes)) {
      const updateData = { status: outcome };
      if (notes[bookingId]) updateData.transporter_note = notes[bookingId];

      await supabase.from('bookings').update(updateData).eq('id', bookingId);

      try {
        const booking = tripBookings.find(b => b.id === bookingId);
        if (booking) {
          await supabase.functions.invoke('send-notification', {
            body: {
              type: 'UPDATE',
              record: { ...booking, status: outcome, transporter_note: notes[bookingId] || null },
              old_record: { ...booking },
            },
          });
        }
      } catch (err) {
        console.error('Notification error:', err);
      }
    }

    await supabase.from('trips').update({ status: 'completed' }).eq('id', tripId);
    fetchData();
  }

  async function submitWithdrawal() {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) return;
    if (parseFloat(withdrawalAmount) > (wallet?.balance || 0)) {
      alert(t('dashboard.alertBalanceExceeded'));
      return;
    }
    setWithdrawalSubmitting(true);
    const { error } = await supabase.from('withdrawal_requests').insert({
      user_id: user.id,
      wallet_id: wallet.id,
      amount: parseFloat(withdrawalAmount),
      currency: 'EUR',
      bank_country: withdrawalForm.bank_country,
      account_holder: withdrawalForm.account_holder,
      iban: withdrawalForm.bank_country === 'DE' ? withdrawalForm.iban : null,
      bic: withdrawalForm.bank_country === 'DE' ? withdrawalForm.bic : null,
      bank_name: withdrawalForm.bank_country === 'TN' ? withdrawalForm.bank_name : null,
      account_number: withdrawalForm.bank_country === 'TN' ? withdrawalForm.account_number : null,
    });
    setWithdrawalSubmitting(false);
    if (!error) {
      setWithdrawalSuccess(true);
      setShowWithdrawalForm(false);
      setWithdrawalAmount('');
      setWithdrawalForm({ account_holder: '', bank_country: 'DE', iban: '', bic: '', bank_name: '', account_number: '' });
    } else {
      alert(t('dashboard.alertPaymentError'));
    }
  }

  const isTransporter = profile?.role === 'transporter' || profile?.role === 'both';
  const { t } = useLang();
  const STATUS_LABELS = getStatusLabels(t);
  const COMPLETION_OUTCOMES = getCompletionOutcomes(t);
  const isSender = profile?.role === 'sender' || profile?.role === 'both';
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="page dashboard-page">
      <div className="container">
        <div className="db-header">
          <div>
            <h1>{t('dashboard.title')}</h1>
            <p>Welcome back, {profile?.full_name?.split(' ')[0]} 👋</p>
          </div>
          {isTransporter && (
            <Link to="/post-trip" className="btn btn-primary">+ Post New Trip</Link>
          )}
        </div>

        {/* PAYMENT SUCCESS BANNER */}
        {paymentSuccess && (
          <div style={{
            background: 'rgba(39,174,96,0.12)',
            border: '1px solid rgba(39,174,96,0.3)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <span style={{ fontSize: '24px' }}>🎉</span>
            <div>
              <div style={{ fontWeight: '700', color: '#2ecc71', fontSize: '15px' }}>Payment successful!</div>
              <div style={{ fontSize: '13px', color: 'rgba(245,237,216,0.7)', marginTop: '2px' }}>
                Your booking is confirmed. The transporter has been notified and your wallet has been updated.
              </div>
            </div>
            <button
              onClick={() => setSearchParams({})}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(245,237,216,0.4)', cursor: 'pointer', fontSize: '18px', padding: '4px' }}
            >✕</button>
          </div>
        )}
        {cashBookingSuccess && (
          <div style={{
            background: 'rgba(232,197,71,0.1)',
            border: '1px solid rgba(232,197,71,0.3)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <span style={{ fontSize: '24px' }}>💵</span>
            <div>
              <div style={{ fontWeight: '700', color: 'rgba(232,197,71,0.9)', fontSize: '15px' }}>Cash booking confirmed!</div>
              <div style={{ fontSize: '13px', color: 'rgba(245,237,216,0.7)', marginTop: '2px' }}>
                Your booking is confirmed. Pay the transporter in cash at pickup.
              </div>
            </div>
            <button
              onClick={() => setSearchParams({})}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(245,237,216,0.4)', cursor: 'pointer', fontSize: '18px', padding: '4px' }}
            >✕</button>
          </div>
        )}


        {/* ══════════════════════════════════════════════════════
            OVERVIEW PANEL — role-aware stats + quick actions
        ══════════════════════════════════════════════════════ */}
        {!loading && (() => {
          /* ── TRANSPORTER stats ── */
          if (isTransporter) {
            const activeTrips     = trips.filter(t => ['active','full'].includes(t.status));
            const completedTrips  = trips.filter(t => t.status === 'completed');
            const activeBookings  = tripBookings.filter(b => ['confirmed','collected','departed'].includes(b.status));
            const pendingRequests = tripBookings.filter(b => b.status === 'pending' || b.status === 'proposed').length + proposals.length;
            const totalKgBooked   = activeBookings.reduce((s,b) => s + (b.kg_booked || 0), 0);
            const walletBalance   = wallet?.balance ?? 0;
            const lifetimeEarned  = walletTransactions.filter(tx => tx.direction === 'credit').reduce((s,tx) => s + tx.amount, 0);
            const nextTrip        = activeTrips.find(t => t.departure_date);
            const hasUnpaidAction = tripBookings.some(b => b.status === 'pending');

            return (
              <div style={{ marginBottom: '24px' }}>
                {/* Quick action banner */}
                {pendingRequests > 0 && (
                  <div
                    onClick={() => setTab('requests')}
                    style={{ cursor: 'pointer', background: 'rgba(232,52,26,0.08)', border: '1px solid rgba(232,52,26,0.25)', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}
                  >
                    <span style={{ fontSize: '18px' }}>🔔</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--sand)' }}>
                        {pendingRequests} booking request{pendingRequests > 1 ? 's' : ''} waiting for your response
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>Tap to review and accept or decline</div>
                    </div>
                    <span style={{ color: 'var(--red)', fontSize: '18px', fontWeight: '700' }}>→</span>
                  </div>
                )}

                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  {[
                    { label: 'Wallet balance', value: `€${walletBalance.toFixed(2)}`, sub: `€${lifetimeEarned.toFixed(2)} lifetime`, icon: '💰', onClick: () => setTab('wallet'), accent: true },
                    { label: 'Active trips', value: activeTrips.length, sub: `${completedTrips.length} completed`, icon: '🚚', onClick: () => setTab('trips') },
                    { label: 'Active bookings', value: activeBookings.length, sub: `${totalKgBooked} kg in transit`, icon: '📦', onClick: () => setTab('requests') },
                    { label: 'Pending requests', value: pendingRequests, sub: pendingRequests > 0 ? 'Need your response' : 'All clear', icon: '📬', onClick: () => setTab('requests'), urgent: pendingRequests > 0 },
                  ].map((s, i) => (
                    <div key={i} onClick={s.onClick} style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: `1px solid ${s.urgent ? 'rgba(232,52,26,0.3)' : s.accent ? 'rgba(232,197,71,0.2)' : 'rgba(255,255,255,0.07)'}`, borderRadius: '14px', padding: '14px 16px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    >
                      <div style={{ fontSize: '18px', marginBottom: '6px' }}>{s.icon}</div>
                      <div style={{ fontFamily: 'Syne', fontWeight: '800', fontSize: '22px', letterSpacing: '-0.5px', color: s.urgent ? 'var(--red)' : s.accent ? '#E8C547' : 'var(--sand)' }}>{s.value}</div>
                      <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(245,237,216,0.4)', marginTop: '2px' }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Next departure */}
                {nextTrip && (
                  <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '22px' }}>✈️</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', color: 'var(--olive)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>Next departure</div>
                      <div style={{ fontWeight: '700', fontSize: '15px' }}>{nextTrip.origin_city} → {nextTrip.destination_city}</div>
                      <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>
                        {new Date(nextTrip.departure_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {' · '}{nextTrip.available_kg} kg still available
                        {nextTrip.is_multi_stop && <span style={{ marginLeft: '6px', fontSize: '10px', background: 'rgba(232,52,26,0.15)', border: '1px solid rgba(232,52,26,0.3)', color: 'var(--red)', borderRadius: '100px', padding: '1px 7px', fontWeight: '700' }}>🛑 multi-stop</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {nextTrip.is_multi_stop && (
                        <a href={`/trip/${nextTrip.id}/itinerary`} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 12px' }}>📤 Share</a>
                      )}
                      <button className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 12px' }} onClick={() => { setSelectedTrip(nextTrip); setTab('trips'); }}>View →</button>
                    </div>
                  </div>
                )}

                {/* Quick actions */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Link to="/post-trip" className="btn btn-primary" style={{ fontSize: '13px' }}>+ New trip</Link>
                  <Link to="/browse-requests" className="btn btn-ghost" style={{ fontSize: '13px' }}>📋 Browse requests</Link>
                  <button className="btn btn-ghost" style={{ fontSize: '13px' }} onClick={() => setTab('wallet')}>💰 Wallet</button>
                  <button className="btn btn-ghost" style={{ fontSize: '13px' }} onClick={() => setTab('messages')}>
                    💬 Messages {totalUnread > 0 && <span style={{ background: 'var(--red)', color: '#fff', borderRadius: '100px', padding: '1px 6px', fontSize: '10px', marginLeft: '4px' }}>{totalUnread}</span>}
                  </button>
                </div>
              </div>
            );
          }

          /* ── SENDER stats ── */
          if (isSender && !isTransporter || (isSender && isTransporter && tab === 'bookings')) {
            const activeBookings   = bookings.filter(b => ['confirmed','collected','departed'].includes(b.status));
            const pendingBookings  = bookings.filter(b => ['pending','proposed'].includes(b.status));
            const deliveredCount   = bookings.filter(b => b.status === 'delivered').length;
            const totalSpent       = bookings.filter(b => ['confirmed','collected','departed','delivered'].includes(b.status) && ['paid','cash'].includes(b.payment_status)).reduce((s,b) => s + (b.total_price || 0), 0);
            const incomingProposals = senderProposals.filter(p => p.status === 'proposed');
            const openRequests     = senderMyRequests.filter(r => r.status === 'open').length;

            return (
              <div style={{ marginBottom: '24px' }}>
                {/* Proposal alert */}
                {incomingProposals.length > 0 && (
                  <div
                    onClick={() => setTab('bookings')}
                    style={{ cursor: 'pointer', background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.25)', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}
                  >
                    <span style={{ fontSize: '18px' }}>✈️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--sand)' }}>
                        {incomingProposals.length} transporter{incomingProposals.length > 1 ? 's have' : ' has'} offered to carry your goods
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>Review and pay to confirm your booking</div>
                    </div>
                    <span style={{ color: '#4CAF7D', fontSize: '18px', fontWeight: '700' }}>→</span>
                  </div>
                )}

                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  {[
                    { label: 'In transit', value: activeBookings.length, sub: activeBookings.length > 0 ? 'Goods on the move' : 'Nothing active', icon: '🚚', onClick: () => setTab('bookings') },
                    { label: 'Awaiting confirm', value: pendingBookings.length, sub: pendingBookings.length > 0 ? 'Pending payment' : 'All confirmed', icon: '⏳', onClick: () => setTab('bookings'), urgent: pendingBookings.length > 0 },
                    { label: 'Delivered', value: deliveredCount, sub: 'All time', icon: '✅', onClick: () => setTab('bookings') },
                    { label: 'Total spent', value: `€${totalSpent.toFixed(2)}`, sub: `${bookings.length} bookings total`, icon: '💳', onClick: () => setTab('bookings') },
                  ].map((s, i) => (
                    <div key={i} onClick={s.onClick} style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: `1px solid ${s.urgent ? 'rgba(232,52,26,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: '14px', padding: '14px 16px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    >
                      <div style={{ fontSize: '18px', marginBottom: '6px' }}>{s.icon}</div>
                      <div style={{ fontFamily: 'Syne', fontWeight: '800', fontSize: '22px', letterSpacing: '-0.5px', color: s.urgent ? 'var(--red)' : 'var(--sand)' }}>{s.value}</div>
                      <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(245,237,216,0.4)', marginTop: '2px' }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Active shipments mini-list */}
                {activeBookings.length > 0 && (
                  <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '14px 16px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--olive)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Active shipments</div>
                    {activeBookings.slice(0, 3).map(b => {
                      const statusColor = b.status === 'departed' ? '#4CAF7D' : b.status === 'collected' ? '#E8C547' : 'var(--olive)';
                      return (
                        <div key={b.id} onClick={() => setSelectedBooking(b)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>{b.trips?.origin_city} → {b.trips?.destination_city}</div>
                            <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '1px' }}>{b.kg_booked} kg · {b.trips?.profiles?.full_name}</div>
                          </div>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: statusColor, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{b.status}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Quick actions */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Link to="/search" className="btn btn-primary" style={{ fontSize: '13px' }}>🔍 Find a transporter</Link>
                  <Link to="/browse-requests" className="btn btn-ghost" style={{ fontSize: '13px' }}>📋 Post a request</Link>
                  <button className="btn btn-ghost" style={{ fontSize: '13px' }} onClick={() => setTab('messages')}>
                    💬 Messages {totalUnread > 0 && <span style={{ background: 'var(--red)', color: '#fff', borderRadius: '100px', padding: '1px 6px', fontSize: '10px', marginLeft: '4px' }}>{totalUnread}</span>}
                  </button>
                </div>
              </div>
            );
          }

          return null;
        })()}

        <div className="db-tabs">
          {isSender && (
            <button className={'db-tab' + (tab === 'bookings' ? ' active' : '')} onClick={() => setTab('bookings')}>
              My Bookings <span className="tab-count">{bookings.length}</span>
            </button>
          )}
          {isTransporter && (
            <>
              <button className={'db-tab' + (tab === 'trips' ? ' active' : '')} onClick={() => setTab('trips')}>
                My Trips <span className="tab-count">{trips.length}</span>
              </button>
              <button className={'db-tab' + (tab === 'requests' ? ' active' : '')} onClick={() => setTab('requests')}>
                Booking Requests <span className="tab-count">{
                  tripBookings.filter(b => !['cancelled','rejected','delivered','could_not_deliver','lost','seized_by_customs','returned_to_sender'].includes(b.status)).length +
                  proposals.length
                }</span>
              </button>
            </>
          )}
          {isTransporter && (
            <button className={'db-tab' + (tab === 'wallet' ? ' active' : '')} onClick={() => setTab('wallet')}>
              💰 Wallet
            </button>
          )}

          <button className={'db-tab' + (tab === 'messages' ? ' active' : '')} onClick={() => setTab('messages')}>
            Messages
            {totalUnread > 0 && <span className="tab-count tab-count-unread">{totalUnread}</span>}
            {totalUnread === 0 && <span className="tab-count">0</span>}
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><div className="spinner" /></div>
        ) : (
          <>
            {/* MY BOOKINGS */}
            {tab === 'bookings' && (
              <div>
                {bookings.length === 0 ? (
                  <div className="db-empty">
                    <div style={{ fontSize: '40px' }}>📦</div>
                    <h3>{t('dashboard.noBookings')}</h3>
                    <p>{t('dashboard.noBookingsDesc')}</p>
                    <Link to="/search" className="btn btn-primary" style={{ marginTop: '16px' }}>Browse Trips</Link>
                  </div>
                ) : (
                  <div className="db-list">
                    {bookings.map(b => (
                      <div className="db-item db-item-clickable" key={b.id} onClick={() => setSelectedBooking(b)}>
                        <div className="db-item-left">
                          <div className="db-item-route">{b.trips?.origin_city} to {b.trips?.destination_city}</div>
                          <div className="db-item-meta">
                            {b.trips?.departure_date && format(new Date(b.trips.departure_date), 'MMM d, yyyy')} · {b.kg_booked > 0 ? `${b.kg_booked} kg` : ''}{b.document_count > 0 ? `${b.kg_booked > 0 ? ' + ' : ''}${b.document_count} doc` : ''} · Transporter: {b.trips?.profiles?.full_name}
                          </div>
                        </div>
                        <div className="db-item-right">
                          <div className="db-item-price">€{b.total_price?.toFixed(2)}</div>
                          <span className={'badge ' + STATUS_LABELS[b.status]?.class}>{STATUS_LABELS[b.status]?.label}</span>
                          {b.status === 'delivered' && !reviewedBookingIds.has(b.id) && (
                            <Link to={`/review/${b.id}`} className="btn btn-outline" style={{ fontSize: '12px', padding: '6px 12px' }} onClick={e => e.stopPropagation()}>
                              ⭐ Review
                            </Link>
                          )}
                          <Link to={'/chat/' + b.id} className="btn btn-ghost db-chat-btn" onClick={e => e.stopPropagation()}>
                            💬 Chat
                            {unreadCounts[b.id] > 0 && <span className="unread-dot">{unreadCounts[b.id]}</span>}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* MY TRIPS */}
            {tab === 'trips' && isTransporter && (
              <div>
                {trips.length === 0 ? (
                  <div className="db-empty">
                    <div style={{ fontSize: '40px' }}>✈️</div>
                    <h3>{t('dashboard.noTrips')}</h3>
                    <p>{t('dashboard.noTripsDesc')}</p>
                    <Link to="/post-trip" className="btn btn-primary" style={{ marginTop: '16px' }}>Post a Trip</Link>
                  </div>
                ) : (() => {
                  const activeTrips = trips.filter(t => ['active', 'full'].includes(t.status));
                  const archivedTrips = trips.filter(t => ['cancelled', 'completed'].includes(t.status));
                  return (
                    <div>
                      {/* ACTIVE TRIPS */}
                      {activeTrips.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '32px', color: 'var(--olive)', fontSize: '14px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', marginBottom: '16px' }}>
                          No active trips — <Link to="/post-trip" style={{ color: 'var(--red)' }}>post one</Link>
                        </div>
                      )}
                      <div className="db-list">
                        {activeTrips.map(trip => (
                          <TripRow
                            key={trip.id}
                            trip={trip}
                            shareMenuTripId={shareMenuTripId}
                            setShareMenuTripId={setShareMenuTripId}
                            setSelectedTrip={setSelectedTrip}
                          />
                        ))}
                      </div>

                      {/* ARCHIVED TRIPS — collapsed by default */}
                      {archivedTrips.length > 0 && (
                        <div style={{ marginTop: '24px' }}>
                          <button
                            onClick={() => setShowArchivedTrips(s => !s)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: 'var(--olive)', fontSize: '13px', fontWeight: '600',
                              padding: '0 0 12px 0', width: '100%', textAlign: 'left',
                            }}
                          >
                            <span style={{ transition: 'transform 0.2s', display: 'inline-block', transform: showArchivedTrips ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
                            {showArchivedTrips ? 'Hide' : 'Show'} archived trips ({archivedTrips.length})
                          </button>
                          {showArchivedTrips && (
                            <div className="db-list">
                              {archivedTrips.map(trip => (
                                <TripRow
                                  key={trip.id}
                                  trip={trip}
                                  shareMenuTripId={shareMenuTripId}
                                  setShareMenuTripId={setShareMenuTripId}
                                  setSelectedTrip={setSelectedTrip}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* BOOKING REQUESTS */}
            {tab === 'requests' && isTransporter && (
              <div>
                {/* Outgoing proposals — transporter waiting for sender to pay */}
                {proposals.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontFamily: 'Syne', fontSize: '15px', fontWeight: '700', marginBottom: '12px', color: 'var(--olive)' }}>
                      📬 My Proposals ({proposals.length})
                    </h3>
                    <div className="db-list">
                      {proposals.map(b => {
                        const req = b.shipment_requests;
                        const trip = b.trips;
                        return (
                          <div key={b.id} className="db-item" style={{ borderLeft: '3px solid rgba(232,197,71,0.4)' }}>
                            <div className="db-item-left">
                              <div className="db-item-route">
                                {trip?.origin_city} → {trip?.destination_city}
                                <span style={{ fontSize: '12px', color: 'var(--olive)', marginLeft: '8px' }}>
                                  for {req?.profiles?.full_name?.split(' ')[0] || 'sender'}
                                </span>
                              </div>
                              <div className="db-item-meta">
                                {req?.kg_needed} kg · Departs {trip?.departure_date ? format(new Date(trip.departure_date), 'MMM d, yyyy') : '—'}
                                {' · '}{req?.description}
                              </div>
                              <div style={{ fontSize: '12px', color: 'rgba(232,197,71,0.7)', marginTop: '4px' }}>
                                ⏳ Waiting for sender to review and pay
                              </div>
                            </div>
                            <div className="db-item-right">
                              <div className="db-item-price">€{(b.total_price - b.platform_fee)?.toFixed(2)} <span style={{ color: 'var(--olive)', fontSize: '11px' }}>you earn</span></div>
                              <span className="badge badge-yellow">📬 Proposed</span>
                              <button
                                className="btn btn-ghost"
                                style={{ fontSize: '12px', padding: '6px 12px', color: 'rgba(232,87,74,0.8)', marginTop: '6px' }}
                                onClick={() => handleWithdrawProposal(b)}
                              >
                                Withdraw
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ borderBottom: '1px solid var(--border)', margin: '20px 0' }} />
                  </div>
                )}

                {(() => {
                  // Exclude bookings that are request-based proposals — already shown above
                  const directBookings = tripBookings.filter(b =>
                    !(b.shipment_request_id && b.status === 'proposed')
                  );
                  if (directBookings.length === 0 && proposals.length === 0) return (
                    <div className="db-empty">
                      <div style={{ fontSize: '40px' }}>📬</div>
                      <h3>{t('dashboard.noRequests')}</h3>
                      <p>{t('dashboard.noRequestsDesc')}</p>
                    </div>
                  );
                  if (directBookings.length === 0) return null;
                  return (
                  <div className="db-list">
                    {directBookings.map(b => (
                      <div className="db-item db-item-clickable" key={b.id} onClick={() => setSelectedBooking({ ...b, isTransporterView: true })}>
                        <div className="db-item-left">
                          <div className="db-item-route">
                            <Link to={`/sender/${b.sender_id}`} style={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: 'rgba(245,237,216,0.3)' }} onClick={e => e.stopPropagation()}>
                              {b.sender?.full_name || b.profiles?.full_name || 'Sender'}
                            </Link>
                            {(b.sender?.phone || b.profiles?.phone) && (
                              <span style={{ color: 'var(--olive)', fontSize: '12px', marginLeft: '6px' }}>
                                · 📞 {b.sender?.phone || b.profiles?.phone}
                              </span>
                            )}
                            <span style={{ color: 'rgba(245,237,216,0.6)' }}>{' '}wants {b.kg_booked} kg</span>
                          </div>
                          <div className="db-item-meta">
                            {b.description}
                            {b.recipient_name && ` · Recipient: ${b.recipient_name}${b.recipient_phone ? ` (${b.recipient_phone})` : ''}`}
                            {b.pickup_address && ' · Pickup: ' + b.pickup_address}
                          </div>
                        </div>
                        <div className="db-item-right">
                          <div className="db-item-price">€{(b.total_price - b.platform_fee)?.toFixed(2)} <span style={{ color: 'var(--olive)', fontSize: '11px' }}>you earn</span></div>
                          <span className={'badge ' + STATUS_LABELS[b.status]?.class}>{STATUS_LABELS[b.status]?.label}</span>
                          {b.status === 'delivered' && !reviewedBookingIds.has(b.id) && (
                            <Link to={`/review/${b.id}`} className="btn btn-outline" style={{ fontSize: '12px', padding: '6px 12px' }} onClick={e => e.stopPropagation()}>
                              ⭐ Review
                            </Link>
                          )}
                          <Link to={'/chat/' + b.id} className="btn btn-ghost db-chat-btn" onClick={e => e.stopPropagation()}>
                            💬 Chat
                            {unreadCounts[b.id] > 0 && <span className="unread-dot">{unreadCounts[b.id]}</span>}
                          </Link>
                          {b.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }} onClick={e => e.stopPropagation()}>
                              <button onClick={() => updateBookingStatus(b.id, 'confirmed')} className="btn btn-primary" style={{ fontSize: '12px', padding: '7px 14px' }}>{t('dashboard.confirmBtn')}</button>
                              <button onClick={() => updateBookingStatus(b.id, 'cancelled', 'transporter')} className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 14px' }}>{t('dashboard.declineBtn')}</button>
                            </div>
                          )}
                          {b.status === 'confirmed' && (
                            <button onClick={e => { e.stopPropagation(); updateBookingStatus(b.id, 'collected'); }} className="btn btn-outline" style={{ fontSize: '12px', padding: '7px 14px', marginTop: '8px' }}>Mark Collected</button>
                          )}
                          {b.status === 'collected' && (
                            <button onClick={e => { e.stopPropagation(); updateBookingStatus(b.id, 'delivered'); }} className="btn btn-outline" style={{ fontSize: '12px', padding: '7px 14px', marginTop: '8px' }}>Mark Delivered</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            )}

            {/* WALLET */}
            {tab === 'saved' && (
              <div>
                {favorites.length === 0 ? (
                  <div className="db-empty">
                    <div style={{ fontSize: '40px' }}>❤️</div>
                    <h3>No saved transporters yet</h3>
                    <p>Visit a transporter's profile and tap "Save transporter" to add them here.</p>
                    <Link to="/search" className="btn btn-primary" style={{ marginTop: '16px' }}>Browse Trips</Link>
                  </div>
                ) : (
                  <div className="db-list">
                    {favorites.map(fav => {
                      const t = fav.profiles;
                      const stars = '★'.repeat(Math.round(t?.rating || 0)) + '☆'.repeat(5 - Math.round(t?.rating || 0));
                      return (
                        <div key={fav.id} className="db-item">
                          <div className="db-item-left">
                            <div className="db-item-route" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--red), #c82d16)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne', fontWeight: '700', color: 'white', fontSize: '14px', flexShrink: 0 }}>
                                {t?.full_name?.[0] || '?'}
                              </div>
                              <div>
                                <div>{t?.full_name}</div>
                                <div style={{ fontSize: '12px', color: 'var(--olive)', fontFamily: 'DM Sans', fontWeight: '400' }}>{t?.city}, Germany</div>
                              </div>
                            </div>
                            <div style={{ marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <span className="stars" style={{ fontSize: '12px' }}>{stars}</span>
                              <span style={{ fontSize: '12px', color: 'var(--olive)' }}>{t?.total_reviews || 0} reviews</span>
                              {t?.is_verified && <span className="badge badge-green" style={{ fontSize: '10px' }}>✓ Verified</span>}
                            </div>
                          </div>
                          <div className="db-item-right">
                            <Link to={`/transporter/${t?.id}`} className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 12px' }}>{t('dashboard.viewProfile')}</Link>
                            <Link to={`/search`} className="btn btn-primary" style={{ fontSize: '12px', padding: '7px 12px' }}>Book Again →</Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {tab === 'wallet' && isTransporter && (() => {
              // Cash earned = confirmed/active bookings paid in cash (never go through Stripe)
              const ACTIVE = ['confirmed','collected','departed','delivered'];
              // Cash bookings use payment_status='cash' (never go through Stripe)
              // payment_status='paid' = Stripe processed it → already in wallet.balance
              const cashEarned = tripBookings
                .filter(b => b.sender_payment_mode === 'cash' && b.payment_status === 'cash' && ACTIVE.includes(b.status))
                .reduce((sum, b) => sum + (b.total_price - b.platform_fee), 0);
              const onlineBalance = wallet?.balance || 0;
              const totalEarned = onlineBalance + cashEarned;
              return (
              <div>
                {/* Balance cards — always show all 3 */}
                <div className="wallet-cards-grid">

                  {/* Online balance — withdrawable */}
                  <div className="wallet-card-inner" style={{ background: 'linear-gradient(135deg, rgba(232,52,26,0.15), rgba(232,52,26,0.05))', border: '1px solid rgba(232,52,26,0.2)' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.5)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      💳 Online Balance
                    </div>
                    <div className="wallet-balance-amount" style={{ marginBottom: '4px' }}>
                      €{onlineBalance.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.4)', marginBottom: '16px' }}>
                      Withdrawable to your bank account
                    </div>

                  {withdrawalSuccess && (
                    <div style={{ marginBottom: '16px', background: 'rgba(39,174,96,0.15)', border: '1px solid rgba(39,174,96,0.3)', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#2ecc71' }}>
                      ✅ Withdrawal request submitted! We'll process it within 2–3 business days.
                    </div>
                  )}

                  {!showWithdrawalForm ? (
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '13px', padding: '10px 18px' }}
                      disabled={onlineBalance <= 0}
                      onClick={() => { setShowWithdrawalForm(true); setWithdrawalSuccess(false); }}
                    >
                      💸 Request Withdrawal
                    </button>
                  ) : (
                    <div style={{ marginTop: '20px', background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>Withdrawal Request</div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px' }}>Amount (€)</label>
                        <input
                          type="number"
                          min="1"
                          max={onlineBalance}
                          step="0.01"
                          placeholder={`Max €${onlineBalance.toFixed(2)}`}
                          value={withdrawalAmount}
                          onChange={e => setWithdrawalAmount(e.target.value)}
                          style={{ marginTop: '6px' }}
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px' }}>Account holder name</label>
                        <input
                          type="text"
                          placeholder="Full name as on bank account"
                          value={withdrawalForm.account_holder}
                          onChange={e => setWithdrawalForm(f => ({ ...f, account_holder: e.target.value }))}
                          style={{ marginTop: '6px' }}
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px' }}>Bank country</label>
                        <select
                          value={withdrawalForm.bank_country}
                          onChange={e => setWithdrawalForm(f => ({ ...f, bank_country: e.target.value }))}
                          style={{ marginTop: '6px', width: '100%', background: 'var(--card)', color: 'var(--sand)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px 12px' }}
                        >
                          <option value="DE">🇩🇪 Germany (SEPA)</option>
                          <option value="TN">🇹🇳 Tunisia</option>
                        </select>
                      </div>

                      {withdrawalForm.bank_country === 'DE' ? (
                        <>
                          <div className="form-group" style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '12px' }}>IBAN</label>
                            <input type="text" placeholder="DE89 3704 0044 0532 0130 00" value={withdrawalForm.iban} onChange={e => setWithdrawalForm(f => ({ ...f, iban: e.target.value }))} style={{ marginTop: '6px' }} />
                          </div>
                          <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '12px' }}>BIC / SWIFT</label>
                            <input type="text" placeholder="COBADEFFXXX" value={withdrawalForm.bic} onChange={e => setWithdrawalForm(f => ({ ...f, bic: e.target.value }))} style={{ marginTop: '6px' }} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="form-group" style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '12px' }}>Bank name</label>
                            <input type="text" placeholder="e.g. Banque de Tunisie" value={withdrawalForm.bank_name} onChange={e => setWithdrawalForm(f => ({ ...f, bank_name: e.target.value }))} style={{ marginTop: '6px' }} />
                          </div>
                          <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '12px' }}>Account number</label>
                            <input type="text" placeholder="Your account number" value={withdrawalForm.account_number} onChange={e => setWithdrawalForm(f => ({ ...f, account_number: e.target.value }))} style={{ marginTop: '6px' }} />
                          </div>
                        </>
                      )}

                      <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.4)', marginBottom: '16px', lineHeight: '1.6' }}>
                        {withdrawalForm.bank_country === 'DE'
                          ? '🇩🇪 German accounts: processed within 2–3 business days via bank transfer.'
                          : '🇹🇳 Tunisian accounts: processed manually within 3–5 business days.'}
                      </div>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                          disabled={withdrawalSubmitting || !withdrawalAmount || !withdrawalForm.account_holder}
                          onClick={submitWithdrawal}
                        >
                          {withdrawalSubmitting ? 'Submitting...' : 'Submit Request'}
                        </button>
                        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowWithdrawalForm(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                  </div>{/* end online balance card */}

                  {/* Cash earned card — shown only when there are cash bookings */}
                  {/* Cash earnings card — always shown */}
                  <div style={{ background: 'linear-gradient(135deg, rgba(232,197,71,0.1), rgba(232,197,71,0.03))', border: '1px solid rgba(232,197,71,0.25)', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.5)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        💵 Cash Earnings
                      </div>
                      <div className="wallet-balance-amount" style={{ marginBottom: '4px' }}>
                        €{cashEarned.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.4)', lineHeight: '1.6' }}>
                        {cashEarned > 0 ? 'Collected directly from senders.' : 'No cash bookings yet.'}
                      </div>
                    </div>
                    {cashEarned > 0 && (
                      <div style={{ marginTop: '12px', background: 'rgba(232,197,71,0.08)', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: 'rgba(232,197,71,0.7)' }}>
                        💡 Collect at pickup — not withdrawable
                      </div>
                    )}
                    {cashEarned === 0 && (
                      <div style={{ marginTop: '12px', fontSize: '11px', color: 'rgba(245,237,216,0.2)' }}>
                        Cash bookings will appear here
                      </div>
                    )}
                  </div>

                  {/* Total earned card */}
                  <div style={{ background: 'linear-gradient(135deg, rgba(46,204,113,0.1), rgba(46,204,113,0.03))', border: '1px solid rgba(46,204,113,0.2)', borderRadius: '16px', padding: '24px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.5)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      📊 Total Earned
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: '900', color: '#2ecc71', marginBottom: '4px' }}>
                      €{totalEarned.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.4)', lineHeight: '1.8', marginTop: '8px' }}>
                      <div>💳 Online: €{onlineBalance.toFixed(2)}</div>
                      <div>💵 Cash: €{cashEarned.toFixed(2)}</div>
                    </div>
                  </div>

                </div>{/* end balance grid */}

                <div className="tx-history-header">
                  <div style={{ fontWeight: '700', fontSize: '16px' }}>Transaction History</div>
                  {walletTransactions.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select
                        value={exportYear}
                        onChange={e => setExportYear(Number(e.target.value))}
                        style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--sand)', padding: '6px 10px', fontSize: '13px', cursor: 'pointer' }}
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => exportEarningsReport(exportYear)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(232,52,26,0.1)', border: '1px solid rgba(232,52,26,0.3)', borderRadius: '8px', color: '#E8341A', padding: '6px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        📥 Export Tax Report
                      </button>
                    </div>
                  )}
                </div>
                {walletTransactions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--olive)', fontSize: '14px' }}>
                    No transactions yet. Completed bookings will appear here.
                  </div>
                ) : (
                  <>
                  {/* Transaction detail modal */}
                  {selectedTx && (() => {
                    const relatedBooking = [...tripBookings, ...bookings].find(b => b.id === selectedTx.booking_id);
                    const typeLabel = {
                      booking_credit:    '💳 Online booking payment',
                      cash_credit:       '💵 Cash collected',
                      refund_debit:      '↩️ Refund issued',
                      withdrawal_debit:  '💸 Withdrawal',
                      adjustment:        '⚙️ Adjustment',
                      platform_fee_debit:'📊 Platform fee',
                    }[selectedTx.type] || selectedTx.type;
                    const amountColor = selectedTx.type === 'cash_credit' ? '#F5A623' : selectedTx.direction === 'credit' ? '#2ecc71' : '#e8775a';
                    const amountPrefix = selectedTx.type === 'cash_credit' ? '💵 ' : selectedTx.direction === 'credit' ? '+' : '-';
                    return (
                      <div className="modal-backdrop" onClick={() => setSelectedTx(null)}>
                        <div className="modal-box" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                          <div className="modal-header">
                            <h3 style={{ fontFamily: 'Syne', fontWeight: '700' }}>{typeLabel}</h3>
                            <button className="modal-close" onClick={() => setSelectedTx(null)}>✕</button>
                          </div>

                          {/* Amount */}
                          <div style={{ textAlign: 'center', padding: '20px 0', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ fontSize: '40px', fontWeight: '900', color: amountColor }}>
                              {amountPrefix}€{selectedTx.amount.toFixed(2)}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                              {selectedTx.direction === 'credit' ? 'Received' : 'Deducted'}
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                            {/* Date & time */}
                            <div className="modal-row">
                              <span className="modal-label">Date</span>
                              <span>{new Date(selectedTx.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            </div>
                            <div className="modal-row">
                              <span className="modal-label">Time</span>
                              <span>{new Date(selectedTx.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>

                            {/* Type */}
                            <div className="modal-row">
                              <span className="modal-label">Type</span>
                              <span>{typeLabel}</span>
                            </div>

                            {/* Payment method */}
                            <div className="modal-row">
                              <span className="modal-label">Method</span>
                              <span>{selectedTx.type === 'cash_credit' ? '💵 Cash' : '💳 Online'}</span>
                            </div>

                            {/* Description */}
                            {selectedTx.description && (
                              <div className="modal-row">
                                <span className="modal-label">Note</span>
                                <span style={{ color: 'rgba(245,237,216,0.7)' }}>{selectedTx.description}</span>
                              </div>
                            )}

                            {/* Stripe payment intent */}
                            {selectedTx.stripe_payment_intent_id && (
                              <div className="modal-row">
                                <span className="modal-label">Stripe ref</span>
                                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--olive)' }}>{selectedTx.stripe_payment_intent_id}</span>
                              </div>
                            )}

                            {/* Related booking info */}
                            {relatedBooking && (
                              <>
                                <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0', paddingTop: '8px' }}>
                                  <div style={{ fontSize: '11px', color: 'var(--olive)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Related booking</div>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label">Sender</span>
                                  <span>{relatedBooking.sender?.full_name || relatedBooking.profiles?.full_name || '—'}</span>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label">Weight</span>
                                  <span>{relatedBooking.kg_booked} kg</span>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label">Items</span>
                                  <span>{relatedBooking.description || '—'}</span>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label">Total price</span>
                                  <span>€{parseFloat(relatedBooking.total_price || 0).toFixed(2)}</span>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label">Platform fee</span>
                                  <span style={{ color: '#e8775a' }}>−€{parseFloat(relatedBooking.platform_fee || 0).toFixed(2)}</span>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label" style={{ fontWeight: '700' }}>You earned</span>
                                  <span style={{ fontWeight: '700', color: amountColor }}>€{selectedTx.amount.toFixed(2)}</span>
                                </div>
                                <div className="modal-row">
                                  <span className="modal-label">Booking status</span>
                                  <span className={'badge ' + (STATUS_LABELS[relatedBooking.status]?.class || 'badge-gray')}>
                                    {STATUS_LABELS[relatedBooking.status]?.label || relatedBooking.status}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {walletTransactions.map(tx => (
                      <div key={tx.id} onClick={() => setSelectedTx(tx)} style={{ background: 'var(--card)', borderRadius: '12px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}
                      >
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                            {tx.type === 'booking_credit' && '💳 Booking payment'}
                            {tx.type === 'cash_credit' && '💵 Cash collected'}
                            {tx.type === 'refund_debit' && '↩️ Refund issued'}
                            {tx.type === 'withdrawal_debit' && '💸 Withdrawal'}
                            {tx.type === 'adjustment' && '⚙️ Adjustment'}
                            {tx.type === 'platform_fee_debit' && '📊 Platform fee'}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--olive)' }}>
                            {new Date(tx.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                          {tx.description && (
                            <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.4)', marginTop: '2px' }}>{tx.description}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: tx.type === 'cash_credit' ? '#F5A623' : tx.direction === 'credit' ? '#2ecc71' : '#e8775a' }}>
                            {tx.type === 'cash_credit' ? '💵 ' : tx.direction === 'credit' ? '+' : '-'}€{tx.amount.toFixed(2)}
                          </div>
                          <span style={{ color: 'rgba(245,237,216,0.2)', fontSize: '14px' }}>›</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )}
              </div>
              );
            })()}

            {/* MESSAGES */}
            {tab === 'messages' && (
              <div>
                {[...bookings, ...tripBookings].length === 0 ? (
                  <div className="db-empty">
                    <div style={{ fontSize: '40px' }}>💬</div>
                    <h3>{t('dashboard.noMessages')}</h3>
                    <p>{t('dashboard.noMessagesDesc')}</p>
                  </div>
                ) : (
                  <div className="db-list">
                    {(() => {
                      const allConvos = [
                        ...bookings.map(b => ({ ...b, role: 'sender' })),
                        ...tripBookings.map(b => ({ ...b, role: 'transporter' })),
                      ];

                      // Group by the other person's ID
                      const grouped = {};
                      allConvos.forEach(b => {
                        const otherId = b.role === 'sender' ? b.trips?.transporter_id : b.sender_id;
                        if (!grouped[otherId]) grouped[otherId] = [];
                        grouped[otherId].push(b);
                      });

                      return Object.entries(grouped).map(([otherId, convos]) => {
                        const first = convos[0];
                        const otherName = first.role === 'sender' ? first.trips?.profiles?.full_name : first.profiles?.full_name;
                        const totalUnreadForPerson = convos.reduce((sum, b) => sum + (unreadCounts[b.id] || 0), 0);

                        if (convos.length === 1) {
                          const b = first;
                          const route = b.trips ? (b.trips.origin_city + ' → ' + b.trips.destination_city) : '';
                          const unread = unreadCounts[b.id] || 0;
                          return (
                            <Link to={'/chat/' + b.id} key={b.id + b.role} className={'db-item db-convo-item' + (unread > 0 ? ' db-convo-unread' : '')}>
                              <div className="db-convo-avatar">{otherName?.[0] || '?'}</div>
                              <div className="db-item-left">
                                <div className="db-item-route">{otherName}</div>
                                <div className="db-item-meta">{route}</div>
                              </div>
                              <div className="db-item-right">
                                {unread > 0 && <span className="unread-badge">{unread}</span>}
                                <span style={{ color: 'var(--olive)', fontSize: '18px' }}>›</span>
                              </div>
                            </Link>
                          );
                        }

                        // Multiple bookings with same person — group under one header with sub-rows per trip
                        return (
                          <div key={otherId} className="db-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0', padding: '0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px 8px' }}>
                              <div className="db-convo-avatar">{otherName?.[0] || '?'}</div>
                              <div style={{ flex: 1 }}>
                                <div className="db-item-route">{otherName}</div>
                                <div className="db-item-meta">{convos.length} conversations</div>
                              </div>
                              {totalUnreadForPerson > 0 && <span className="unread-badge">{totalUnreadForPerson}</span>}
                            </div>
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                              {convos.map(b => {
                                const route = b.trips ? (b.trips.origin_city + ' → ' + b.trips.destination_city) : '';
                                const dep = b.trips?.departure_date ? format(new Date(b.trips.departure_date), 'MMM d') : '';
                                const unread = unreadCounts[b.id] || 0;
                                return (
                                  <Link
                                    to={'/chat/' + b.id}
                                    key={b.id}
                                    style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      padding: '10px 16px 10px 44px',
                                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                                      textDecoration: 'none',
                                      background: unread > 0 ? 'rgba(232,52,26,0.05)' : 'transparent',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                                    onMouseLeave={e => e.currentTarget.style.background = unread > 0 ? 'rgba(232,52,26,0.05)' : 'transparent'}
                                  >
                                    <div>
                                      <span style={{ fontSize: '13px', color: 'var(--sand)', fontWeight: unread > 0 ? '700' : '400' }}>
                                        ✈️ {route}
                                      </span>
                                      {dep && <span style={{ fontSize: '12px', color: 'var(--olive)', marginLeft: '8px' }}>{dep}</span>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {unread > 0 && <span className="unread-badge">{unread}</span>}
                                      <span style={{ color: 'var(--olive)', fontSize: '16px' }}>›</span>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {selectedBooking && (
        <BookingModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onStatusUpdate={updateBookingStatus}
          isTransporter={!!selectedBooking.isTransporterView}
          unreadCount={unreadCounts[selectedBooking.id] || 0}
          hasReviewed={reviewedBookingIds.has(selectedBooking.id)}
          onCompletePayment={completePayment}
          payingBookingId={payingBookingId}
          transporterProfile={profile}
        />
      )}

      {selectedTrip && (
        <TripModal
          trip={selectedTrip}
          bookings={tripBookings}
          onClose={() => setSelectedTrip(null)}
          onCancelTrip={cancelTrip}
          onStatusUpdate={updateBookingStatus}
          onOpenCompleteTrip={(trip) => { setSelectedTrip(null); setCompleteTripData(trip); }}
        />
      )}
      {completeTripData && (
        <CompleteTripModal
          trip={completeTripData}
          bookings={tripBookings}
          onClose={() => setCompleteTripData(null)}
          onCompleteTrip={completeTrip}
        />
      )}
    </div>
  );
}